import { ChangeEvent, FC, useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { MdInfoOutline } from 'react-icons/md';
import {
  Box,
  Icon,
  IconButton,
  Input,
  InputProps,
  List,
  Stack,
  Text,
} from '@chakra-ui/react';

import { InputGroup, Tooltip } from '~/core/shared/lib/chakraUi/components';

type PasswordInputProps = {
  /* other props for ChildComponent */
  password: string;
  placeholder?: string;
  label?: string;
  tabIndex?: number;
  onPasswordChange: (value: string) => void;
  inputProps?: InputProps;
  tooltip?: boolean;
};

const getPasswordTooltip = () => (
  <Tooltip
    content={
      <List.Root>
        <List.Item>A minimum of 12 characters</List.Item>
        <List.Item>At least one digit (0-9)</List.Item>
        <List.Item>
          Include at least one special character (punctuation)
        </List.Item>
        <List.Item>Include at least one uppercase letter (A-Z)</List.Item>
        <List.Item>Include at least one lowercase letter (a-z)</List.Item>
      </List.Root>
    }
    positioning={{ placement: 'right' }}
    showArrow
  >
    <Icon as={MdInfoOutline} ml={1} />
  </Tooltip>
);

export const PasswordInput: FC<PasswordInputProps> = ({
  password,
  onPasswordChange,
  placeholder,
  label,
  tabIndex,
  inputProps,
  tooltip,
}) => {
  const [show, setShow] = useState<boolean>(false);

  const handleClick = (): void => setShow(!show);
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void =>
    onPasswordChange(event.target.value);

  return (
    <Stack direction="column" gap={4} w="full">
      <Box>
        <Text>
          {label || 'Password* '}
          {tooltip && getPasswordTooltip()}
        </Text>
      </Box>

      <InputGroup
        endElement={
          <IconButton
            aria-label="Show password"
            color="primary.500"
            colorPalette="black"
            h="1.75rem"
            onClick={handleClick}
            variant="outline"
          >
            <Icon as={show ? AiOutlineEyeInvisible : AiOutlineEye} />
          </IconButton>
        }
      >
        <Input
          aria-label={label || 'Password'}
          onChange={handleChange}
          placeholder={placeholder || 'Enter password'}
          pr="4.5rem"
          size="md"
          tabIndex={tabIndex || undefined}
          type={show ? 'text' : 'password'}
          value={password}
          {...inputProps}
        />
      </InputGroup>
    </Stack>
  );
};
